import { DvaReducer } from '@/models/shared';
import { AnyAction } from 'redux';
import { EffectsCommandMap, Subscription } from 'dva';
import { AVATAR_URL_PREFIX } from '@/constants';
import { ConnectState } from '@/models/connect';
import { history } from '@@/core/history';
import { FUtil, FServiceAPI, FI18n } from '@freelog/tools-lib';
// import WXShareMgr from '@/utils/WXShareMgr';

export interface ResourceDetailPageModelState {
  page_State: 'loading' | 'details';
  user_Logged: boolean;
  resource_ID: string;
  resource_Popularity: number;
  resource_IsCollected: boolean;
  resource_Info: {
    title: string;
    cover: string;
    name: string;
    type: string[];
    tags: string[];
    about: string;
    isChoice: boolean;
    username: string;
    useAvatar: string;
    policyTitles: string[];
    error: '' | 'offline' | 'freeze' | 'unreleased';
    warning: '' | 'authException' | 'ownerFreeze';
  };
  resource_baseUpcastResources: {
    id: string;
    title: string;
    cover: string;
    name: string;
    type: string[];
    tags: string[];
    about: string;
    isChoice: boolean;
    username: string;
    useAvatar: string;
    policyTitles: string[];
    error: '' | 'offline' | 'freeze' | 'unreleased';
    warning: '' | 'authException' | 'ownerFreeze';
  }[];
  resource_baseUpcastResources_showMore: boolean;
  propertyKeysSort: string[];

  resourceVersion_SelectedVersion: string;
  resourceVersion_AllVersions: string[];
  resourceVersion_Info: {
    releaseTime: string;
    description: string;
    rawProperties: {
      key: string;
      name: string;
      value: string;
      description: string;
    }[];
    baseProperties: {
      key: string;
      name: string;
      value: string;
      description: string;
    }[];
    customOptions: {
      key: string;
      name: string;
      description: string;
      type: 'input' | 'select';
      input: string;
      select: string[];
    }[];
  };

  resourceVersion_authorizationGraphShow: boolean;
  resourceVersion_dependencyGraphShow: boolean;
}

export interface ChangeAction extends AnyAction {
  type: 'change' | 'resourceDetailPage/change';
  payload: Partial<ResourceDetailPageModelState>;
}

export interface OnMountPageAction extends AnyAction {
  type: 'resourceDetailPage/onMountPage';
  payload: {
    resourceID: string;
    version?: string;
  };
}

export interface OnUnmountPageAction extends AnyAction {
  type: 'resourceDetailPage/onUnmountPage';
}

export interface OnChangeVersionAction extends AnyAction {
  type: 'resourceDetailPage/onChangeVersion';
  payload: {
    version: string;
  };
}

export interface OnClickCollectionAction extends AnyAction {
  type: 'resourceDetailPage/onClickCollection';
}

export interface FetchCollectionInfoAction extends AnyAction {
  type: 'fetchCollectionInfo';
}

export interface FetchVersionInfoAction extends AnyAction {
  type: 'fetchVersionInfo';
}

interface ResourceDetailPageModelType {
  namespace: 'resourceDetailPage';
  state: ResourceDetailPageModelState;
  effects: {
    onMountPage: (action: OnMountPageAction, effects: EffectsCommandMap) => void;
    onUnmountPage: (action: OnUnmountPageAction, effects: EffectsCommandMap) => void;
    onChangeVersion: (action: OnChangeVersionAction, effects: EffectsCommandMap) => void;
    onClickCollection: (action: OnClickCollectionAction, effects: EffectsCommandMap) => void;
    fetchCollectionInfo: (action: FetchCollectionInfoAction, effects: EffectsCommandMap) => void;
    fetchVersionInfo: (action: FetchVersionInfoAction, effects: EffectsCommandMap) => void;
  };
  reducers: {
    change: DvaReducer<ResourceDetailPageModelState, ChangeAction>;
  };
  subscriptions: {
    setup: Subscription;
  };
}

const initStates: ResourceDetailPageModelState = {
  page_State: 'loading',
  user_Logged: FUtil.Tool.getUserIDByCookies() !== -1,

  resource_ID: '',
  resource_Popularity: 0,
  resource_IsCollected: false,
  resource_Info: {
    cover: '',
    title: '',
    name: '',
    type: [],
    tags: [],
    about: '',
    isChoice: false,
    username: '',
    useAvatar: '',
    policyTitles: [],
    error: '',
    warning: '',
  },
  resource_baseUpcastResources: [],
  resource_baseUpcastResources_showMore: false,
  propertyKeysSort: [],
  resourceVersion_SelectedVersion: '',
  resourceVersion_AllVersions: [],
  resourceVersion_Info: {
    releaseTime: '',
    description: '',
    rawProperties: [],
    baseProperties: [],
    customOptions: [],
  },

  resourceVersion_authorizationGraphShow: true,
  resourceVersion_dependencyGraphShow: true,
};

const Model: ResourceDetailPageModelType = {
  namespace: 'resourceDetailPage',
  state: initStates,
  effects: {
    *onMountPage({ payload }: OnMountPageAction, { call, put }: EffectsCommandMap) {
      yield put({
        type: 'change',
        payload: {
          resource_ID: payload.resourceID,
          user_Logged: FUtil.Tool.getUserIDByCookies() !== -1,
        },
      });

      yield put<FetchCollectionInfoAction>({
        type: 'fetchCollectionInfo',
      });

      const params: Parameters<typeof FServiceAPI.Resource.info>[0] = {
        resourceIdOrName: payload.resourceID,
      };

      const {
        data: data_ResourceDetail,
      }: {
        data: {
          resourceId: string;
          resourceName: string;
          resourceTitle: string;
          resourceType: string[];
          resourceTypeCode: string;
          coverImages: string[];
          resourceVersions: {
            createDate: string;
            version: string;
            versionId: string;
          }[];
          status: number;
          operationType: number;
          baseUpcastResources: {
            resourceId: string;
            resourceName: string;
          }[];
          createDate: string;
          updateDate: string;
          latestVersion: string;
          tags: string[];
          policies: {
            policyId: string;
            policyName: string;
            status: 0 | 1;
          }[];
          intro: string;
          userId: number;
          username: string;
          subjectType: 1 | 4;
        };
      } = yield call(FServiceAPI.Resource.info, params);

      if ((data_ResourceDetail.status & 2) === 2) {
        history.replace(FUtil.LinkTo.resourceFreeze({ resourceID: payload.resourceID }));
        return;
      }

      if (data_ResourceDetail.latestVersion === '') {
        history.replace(FUtil.LinkTo.exceptionUnableToAccess());
        return;
      }

      if (data_ResourceDetail.subjectType === 4) {
        history.replace(FUtil.LinkTo.collectionDetails({ collectionID: payload.resourceID }));
        return;
      }

      let resource_baseUpcastResources: ResourceDetailPageModelState['resource_baseUpcastResources'] =
        [];
      if ((data_ResourceDetail.baseUpcastResources || []).length > 0) {
        const params1: Parameters<typeof FServiceAPI.Resource.batchInfo>[0] = {
          resourceIds: data_ResourceDetail.baseUpcastResources
            .map((bur) => {
              return bur.resourceId;
            })
            .join(','),
        };

        const {
          data: data_batchInfo,
        }: {
          data: {
            resourceId: string;
            resourceName: string;
            resourceTitle: string;
            resourceType: string[];
            resourceTypeCode: string;
            coverImages: string[];
            resourceVersions: {
              createDate: string;
              version: string;
              versionId: string;
            }[];
            status: number;
            operationType: number;
            baseUpcastResources: {
              resourceId: string;
              resourceName: string;
            }[];
            createDate: string;
            updateDate: string;
            latestVersion: string;
            tags: string[];
            policies: {
              policyId: string;
              policyName: string;
              status: 0 | 1;
            }[];
            intro: string;
            userId: number;
            username: string;
          }[];
        } = yield call(FServiceAPI.Resource.batchInfo, params1);

        const params2: Parameters<typeof FServiceAPI.Resource.batchAuth>[0] = {
          resourceIds: data_batchInfo
            .map((dbi) => {
              return dbi.resourceId;
            })
            .join(','),
        };
        const {
          data: data_auth,
        }: {
          data: {
            isAuth: boolean;
            resourceId: string;
          }[];
        } = yield call(FServiceAPI.Resource.batchAuth, params2);

        const params3: Parameters<typeof FServiceAPI.User.batchUserList>[0] = {
          userIds: data_batchInfo
            .map((dbi) => {
              return dbi.userId;
            })
            .join(','),
        };

        const {
          data: data_batchUserList,
        }: {
          data: {
            status: number;
            userId: number;
          }[];
        } = yield call(FServiceAPI.User.batchUserList, params3);

        resource_baseUpcastResources = data_batchInfo.map((dbi) => {
          const isAuth: boolean =
            data_auth.find((da) => {
              return da.resourceId === dbi.resourceId;
            })?.isAuth || false;
          const ownerFreeze: boolean =
            data_batchUserList.find((dbu) => {
              return dbu.userId === dbi.userId;
            })?.status === 1;
          return {
            id: dbi.resourceId,
            cover:
              dbi.coverImages.length > 0
                ? dbi.coverImages[0]
                : FUtil.Domain.STATIC_ORIGIN + '/static/default_cover.png',
            name: dbi.resourceName,
            title: dbi.resourceTitle || '',
            type: dbi.resourceType,
            tags: dbi.tags,
            about: dbi.intro,
            isChoice: dbi.operationType === 5,
            username: dbi.username,
            useAvatar: `${AVATAR_URL_PREFIX}${dbi.userId}`,
            policyTitles: dbi.policies
              .filter((p) => {
                return p.status === 1;
              })
              .map((p) => {
                return p.policyName;
              }),
            error:
              dbi.status === 0
                ? 'unreleased'
                : dbi.status === 2
                ? 'freeze'
                : dbi.status === 4
                ? 'offline'
                : '',
            warning: ownerFreeze ? 'ownerFreeze' : !isAuth ? 'authException' : '',
          };
        });
      }

      const params1: Parameters<typeof FServiceAPI.Resource.batchAuth>[0] = {
        resourceIds: payload.resourceID,
      };
      const {
        data: data_auth,
      }: {
        data: {
          isAuth: boolean;
          resourceId: string;
        }[];
      } = yield call(FServiceAPI.Resource.batchAuth, params1);
      // console.log(data_auth, 'data_auth sdifjlsdkjfljlkj');

      const params3: Parameters<typeof FServiceAPI.User.batchUserList>[0] = {
        userIds: String(data_ResourceDetail.userId),
      };

      const {
        data: data_batchUserList,
      }: {
        data: {
          status: number;
          userId: number;
        }[];
      } = yield call(FServiceAPI.User.batchUserList, params3);
      // console.log(data_batchUserList, 'data_batchUserList sdlkfjlsdjlkfjl');

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          page_State: 'details',
          resource_Info: {
            cover:
              data_ResourceDetail.coverImages.length > 0
                ? data_ResourceDetail.coverImages[0]
                : FUtil.Domain.STATIC_ORIGIN + '/static/default_cover.png',
            name: data_ResourceDetail.resourceName,
            title: data_ResourceDetail.resourceTitle || '',
            type: data_ResourceDetail.resourceType,
            tags: data_ResourceDetail.tags,
            about: data_ResourceDetail.intro,
            isChoice: data_ResourceDetail.operationType === 5,
            username: data_ResourceDetail.username,
            useAvatar: `${AVATAR_URL_PREFIX}${data_ResourceDetail.userId}`,
            policyTitles: data_ResourceDetail.policies
              .filter((p) => {
                return p.status === 1;
              })
              .map((p) => {
                return p.policyName;
              }),
            error:
              data_ResourceDetail.status === 0
                ? 'unreleased'
                : data_ResourceDetail.status === 2
                ? 'freeze'
                : data_ResourceDetail.status === 4
                ? 'offline'
                : '',
            warning:
              data_batchUserList[0].status === 1
                ? 'ownerFreeze'
                : !(data_auth[0] && data_auth[0].isAuth)
                ? 'authException'
                : '',
          },
          resource_baseUpcastResources,
          // propertyKeysSort: data_ResourceTypeInfo.attrs.map((t) => {
          //   return t.key;
          // }),
          resourceVersion_AllVersions: data_ResourceDetail.resourceVersions.map((v) => {
            return v.version;
          }),
          resourceVersion_SelectedVersion: payload.version || data_ResourceDetail.latestVersion,
        },
      });

      yield put<FetchVersionInfoAction>({
        type: 'fetchVersionInfo',
      });
    },
    *onUnmountPage({}: OnUnmountPageAction, { put }: EffectsCommandMap) {
      yield put<ChangeAction>({
        type: 'change',
        payload: initStates,
      });
    },
    *onChangeVersion({ payload }: OnChangeVersionAction, { put }: EffectsCommandMap) {
      yield put({
        type: 'change',
        payload: {
          resourceVersion_SelectedVersion: payload.version,
          resourceVersion_authorizationGraphShow: true,
          resourceVersion_dependencyGraphShow: true,
        },
      });

      yield put<FetchVersionInfoAction>({
        type: 'fetchVersionInfo',
      });
    },

    *onClickCollection({}: OnClickCollectionAction, { select, call, put }: EffectsCommandMap) {
      const { resourceDetailPage }: ConnectState = yield select(
        ({ resourceDetailPage }: ConnectState) => ({
          resourceDetailPage,
        }),
      );

      if (!resourceDetailPage.resource_IsCollected) {
        const params: Parameters<typeof FServiceAPI.Collection.collectResource>[0] = {
          resourceId: resourceDetailPage.resource_ID,
        };
        const { ret, errCode, data } = yield call(FServiceAPI.Collection.collectResource, params);
        // console.log(ret, errCode, data, 'DDDDDDDDDDDDDDDDDDDDDDDDD');
        if (ret !== 0 || errCode !== 0) {
          return;
        }
      } else {
        const params: Parameters<typeof FServiceAPI.Collection.deleteCollectResource>[0] = {
          resourceId: resourceDetailPage.resource_ID,
        };
        const { ret, errCode, data } = yield call(
          FServiceAPI.Collection.deleteCollectResource,
          params,
        );
      }

      yield put<FetchCollectionInfoAction>({
        type: 'fetchCollectionInfo',
      });
    },
    *fetchCollectionInfo({}: FetchCollectionInfoAction, { call, select, put }: EffectsCommandMap) {
      const { resourceDetailPage }: ConnectState = yield select(
        ({ resourceDetailPage }: ConnectState) => ({
          resourceDetailPage,
        }),
      );

      let isCollected: boolean = false;
      if (resourceDetailPage.user_Logged) {
        const params1: Parameters<typeof FServiceAPI.Collection.isCollected>[0] = {
          resourceIds: resourceDetailPage.resource_ID,
        };

        const { data: data1 } = yield call(FServiceAPI.Collection.isCollected, params1);

        isCollected = data1[0].isCollected;
      }

      const params2: Parameters<typeof FServiceAPI.Collection.collectedCount>[0] = {
        resourceId: resourceDetailPage.resource_ID,
      };

      const { data: data2 } = yield call(FServiceAPI.Collection.collectedCount, params2);
      // console.log(data2, '收藏数量FGHSDGf09uj4k2t;ldfs');

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          resource_IsCollected: isCollected,
          resource_Popularity: data2,
        },
      });
    },
    *fetchVersionInfo({}: FetchVersionInfoAction, { call, select, put }: EffectsCommandMap) {
      const { resourceDetailPage }: ConnectState = yield select(
        ({ resourceDetailPage }: ConnectState) => ({
          resourceDetailPage,
        }),
      );

      if (resourceDetailPage.resourceVersion_SelectedVersion === '') {
        return;
      }

      const params: Parameters<typeof FServiceAPI.Resource.resourceVersionInfo1>[0] = {
        version: resourceDetailPage.resourceVersion_SelectedVersion,
        resourceId: resourceDetailPage.resource_ID,
      };
      // console.log('resourceVersionInfo1239weiojfasdlkfjslk');
      const {
        data: data_versionInfo,
      }: {
        data: {
          customPropertyDescriptors: {
            key: string;
            name: string;
            defaultValue: string;
            type: 'editableText' | 'readonlyText' | 'radio' | 'checkbox' | 'select';
            candidateItems: string[];
            remark: string;
          }[];
          systemProperty: { [key: string]: string };
          systemPropertyDescriptors: {
            defaultValue: number | string;
            key: string;
            name: string;
            remark: string;
            valueDisplay: string;
            valueUnit: string;
          }[];
          createDate: string;
          description: string;
        };
      } = yield call(FServiceAPI.Resource.resourceVersionInfo1, params);

      yield put<ChangeAction>({
        type: 'change',
        payload: {
          resourceVersion_Info: {
            releaseTime: FUtil.Format.formatDateTime(data_versionInfo.createDate),
            description: data_versionInfo.description,

            rawProperties: data_versionInfo.systemPropertyDescriptors
              .filter((spd) => {
                return spd.defaultValue !== '';
              })
              .map((spd) => {
                // console.log(spd, 'spdsdifojsd;oifjsld;kjflksdjlkjl');
                return {
                  key: spd.key,
                  name: spd.name,
                  value: spd.valueDisplay,
                  description: spd.remark,
                };
              }),
            baseProperties: data_versionInfo.customPropertyDescriptors
              .filter((p) => {
                return p.type === 'readonlyText' && p.defaultValue !== '';
              })
              .map((p: any) => {
                // console.log(p, 'PPPPP()*UOI');
                return {
                  key: p.key,
                  name: p.name,
                  value: p.defaultValue,
                  description: p.remark,
                };
              }),
            customOptions: data_versionInfo.customPropertyDescriptors
              .filter((i) => {
                return i.type !== 'readonlyText';
              })
              .map((i) => {
                // console.log(p, '@@@@@@#$#@$@#$@#');
                return {
                  key: i.key,
                  name: i.name,
                  description: i.remark,
                  type: i.type === 'editableText' ? 'input' : 'select',
                  input: i.defaultValue,
                  select: i.candidateItems,
                };
              }),
          },
        },
      });
    },
  },
  reducers: {
    change(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },
  },
  subscriptions: {
    setup({}) {},
  },
};

export default Model;
